import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { showBanner } from '../../global-state/banner-slice'
import {
  opportunityNewsServices,
  OpportunityNewsDraft,
  OpportunityNewsItem,
  OpportunityNewsPagination,
} from '../../services/opportunity-news-service'
import { OpportunityNewsListPresenter, NewsModal } from './opportunity-news-list-presenter'

const PAGE_SIZE = 10
const DEFAULT_PAGINATION: OpportunityNewsPagination = {
  total: 0,
  limit: PAGE_SIZE,
  offset: 0,
  count: 0,
}
const EMPTY_DRAFT: OpportunityNewsDraft = {
  headline: '',
  description: '',
  link: '',
  thumbnailUrl: '',
  spotlight: false,
}
const URL_PATTERN = /^https?:\/\/\S+$/i

function OpportunityNewsListContainer() {
  const dispatch = useDispatch()

  const [items, setItems] = useState<OpportunityNewsItem[]>([])
  const [pagination, setPagination] = useState<OpportunityNewsPagination>(DEFAULT_PAGINATION)
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [modal, setModal] = useState<NewsModal>(null)
  const [selected, setSelected] = useState<OpportunityNewsItem | null>(null)
  const [draft, setDraft] = useState<OpportunityNewsDraft>(EMPTY_DRAFT)
  const [draftError, setDraftError] = useState('')
  const [listRefreshKey, setListRefreshKey] = useState(0)

  useEffect(() => {
    const fetchNews = async () => {
      setIsLoading(true)
      const res = await opportunityNewsServices.list({
        limit: PAGE_SIZE,
        offset: (currentPage - 1) * PAGE_SIZE,
      })
      if (res?.status === 200) {
        setItems(res.data.items)
        setPagination(res.data.pagination)
      } else {
        setItems([])
        setPagination(DEFAULT_PAGINATION)
        dispatch(
          showBanner({
            text: res?.data?.message || 'Could not load news. Please try again.',
            variant: 'error-banner',
          }),
        )
      }
      setIsLoading(false)
    }
    fetchNews()
  }, [currentPage, listRefreshKey, dispatch])

  const closeModal = () => {
    setModal(null)
    setSelected(null)
    setDraftError('')
  }

  const handleEdit = (row: OpportunityNewsItem) => {
    setSelected(row)
    setDraft({
      headline: row.headline,
      description: row.description ?? '',
      link: row.link ?? '',
      thumbnailUrl: row.thumbnailUrl ?? '',
      spotlight: row.spotlight,
    })
    setDraftError('')
    setModal('edit')
  }

  const handleDelete = (row: OpportunityNewsItem) => {
    setSelected(row)
    setModal('delete')
  }

  const handleDraftChange = (field: keyof OpportunityNewsDraft, value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }))
  }

  const handleSpotlightChange = (value: boolean) => {
    setDraft((prev) => ({ ...prev, spotlight: value }))
  }

  const handleSave = async () => {
    if (!selected) return
    const headline = draft.headline.trim()
    const link = draft.link.trim()
    const thumbnailUrl = draft.thumbnailUrl.trim()

    if (!headline) {
      setDraftError('Headline is required')
      return
    }
    if ((link && !URL_PATTERN.test(link)) || (thumbnailUrl && !URL_PATTERN.test(thumbnailUrl))) {
      setDraftError('Links must start with http:// or https://')
      return
    }

    setIsSaving(true)
    try {
      const res = await opportunityNewsServices.update(selected.id, {
        headline,
        description: draft.description.trim() || null,
        link: link || null,
        thumbnailUrl: thumbnailUrl || null,
        spotlight: draft.spotlight,
      })
      if (res?.status === 200) {
        closeModal()
        setListRefreshKey((k) => k + 1)
        dispatch(showBanner({ text: 'News updated successfully', variant: 'success-banner' }))
      } else {
        setDraftError(res?.data?.message || 'Something went wrong! Please try again.')
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!selected) return
    setIsDeleting(true)
    try {
      const res = await opportunityNewsServices.remove(selected.id)
      if (res?.status === 204) {
        closeModal()
        setListRefreshKey((k) => k + 1)
        dispatch(showBanner({ text: 'News deleted successfully', variant: 'success-banner' }))
      } else {
        dispatch(
          showBanner({
            text: res?.data?.message || 'Something went wrong! Please try again.',
            variant: 'error-banner',
          }),
        )
      }
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCurrentPage = (page: number) => {
    setCurrentPage(page)
  }

  return (
    <OpportunityNewsListPresenter
      items={items}
      pagination={pagination}
      currentPage={currentPage}
      isLoading={isLoading}
      modal={modal}
      selected={selected}
      draft={draft}
      draftError={draftError}
      isSaving={isSaving}
      isDeleting={isDeleting}
      onDraftChange={handleDraftChange}
      onSpotlightChange={handleSpotlightChange}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onCloseModal={closeModal}
      onSave={handleSave}
      onConfirmDelete={handleConfirmDelete}
      handleCurrentPage={handleCurrentPage}
    />
  )
}

export { OpportunityNewsListContainer }
